import React, { useState } from 'react';
import { toast } from './Toast';
import api from '../services/api';

const RecurringBlockingModal = ({ status, onGenerated }) => {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  if (!status.mustGenerate) return null;

  const nextMonthName = new Date(status.nextMonth + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    
    try {
      const localDate = new Date().toLocaleDateString('en-CA');
      const response = await api.post('/recurring/generate-month', { localDate });
      
      toast.success(`✅ Success! Generated ${response.data.totalCreated} service orders for ${nextMonthName}`);
      
      onGenerated();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.blockHeader}>
          <span style={styles.blockIcon}>🚫</span>
          <h2 style={styles.title}>Action Required</h2>
        </div>
        
        <div style={styles.content}>
          <p style={styles.message}>
            <strong>You cannot proceed without generating recurring services for {nextMonthName}.</strong>
          </p>
          
          <p style={styles.submessage}>
            This is the last day of the month. All recurring service orders must be generated to ensure proper scheduling for next month.
          </p>
          
          <div style={styles.stats}>
            <div style={styles.statItem}>
              <span style={styles.statLabel}>Active Recurrences:</span>
              <span style={styles.statValue}>{status.activeRecurrences}</span>
            </div>
            <div style={styles.statItem}>
              <span style={styles.statLabel}>Target Month:</span>
              <span style={styles.statValue}>{nextMonthName.split(' ')[0]}</span>
            </div>
          </div>

          {error && (
            <div style={styles.error}>
              ❌ Error: {error}
            </div>
          )}
        </div>

        <div style={styles.actions}>
          <button 
            onClick={handleGenerate} 
            disabled={generating}
            style={generating ? {...styles.generateBtn, ...styles.generateBtnDisabled} : styles.generateBtn}
          >
            {generating ? 'Generating...' : `Generate ${nextMonthName} Recurrences Now`}
          </button>
        </div>

        <div style={styles.footer}>
          <small>⚠️ You must complete this action to continue using the system</small>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 3000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '600px',
    boxShadow: '0 10px 60px rgba(0,0,0,0.5)',
    overflow: 'hidden'
  },
  blockHeader: {
    backgroundColor: '#F44336',
    color: 'white',
    padding: '25px',
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  blockIcon: {
    fontSize: '40px'
  },
  title: {
    fontSize: '24px',
    margin: 0
  },
  content: {
    padding: '30px'
  },
  message: {
    fontSize: '18px',
    color: '#333',
    marginBottom: '15px',
    lineHeight: '1.6'
  },
  submessage: {
    fontSize: '15px',
    color: '#666',
    marginBottom: '25px',
    lineHeight: '1.5'
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '15px',
    marginTop: '25px'
  },
  statItem: {
    backgroundColor: '#FFEBEE',
    padding: '20px',
    borderRadius: '8px',
    textAlign: 'center',
    border: '2px solid #F44336'
  },
  statLabel: {
    display: 'block',
    fontSize: '13px',
    color: '#666',
    marginBottom: '8px',
    fontWeight: '600'
  },
  statValue: {
    display: 'block',
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#F44336'
  },
  error: {
    backgroundColor: '#FFEBEE',
    color: '#C62828',
    padding: '15px',
    borderRadius: '8px',
    marginTop: '20px',
    fontSize: '14px',
    border: '1px solid #EF5350'
  },
  actions: {
    padding: '25px',
    borderTop: '2px solid #f0f0f0'
  },
  generateBtn: {
    width: '100%',
    padding: '18px',
    backgroundColor: '#F44336',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  generateBtnDisabled: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed'
  },
  footer: {
    backgroundColor: '#FFF3E0',
    padding: '15px',
    textAlign: 'center',
    color: '#E65100',
    fontSize: '13px',
    fontWeight: '600'
  }
};

export default RecurringBlockingModal;
